import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import CustomAxios from '../utils/CustomAxios';

function auditActor() {
  return (
    (typeof localStorage !== 'undefined' &&
      (localStorage.getItem('username') || localStorage.getItem('membername') || '').trim()) ||
    'user'
  );
}

function templateSelectValue(name, language) {
  const n = (name || '').trim();
  const lang = String(language || '').trim();
  if (!n && !lang) return '';
  return `${n}\x1e${lang}`;
}

function parseTemplateSelectValue(value) {
  if (!value) return { name: '', language: '' };
  const i = value.indexOf('\x1e');
  if (i === -1) return { name: value, language: '' };
  return { name: value.slice(0, i), language: value.slice(i + 1) };
}

/**
 * Timer / scheduler job registry (backend SchedulerConfig) + Azure env reference.
 */
export function CommunicationWhatsAppTriggerConfig() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formJobName, setFormJobName] = useState('');
  const [formEnabled, setFormEnabled] = useState(false);
  const [formTemplateName, setFormTemplateName] = useState('');
  const [formTemplateLanguage, setFormTemplateLanguage] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [waTemplates, setWaTemplates] = useState([]);
  const [waTemplatesLoading, setWaTemplatesLoading] = useState(false);
  const [waTemplatesError, setWaTemplatesError] = useState('');

  const fetchList = useCallback(async () => {
    setListError('');
    setLoading(true);
    try {
      const res = await CustomAxios.get('oasis/scheduler-config/');
      setRows(res.data?.results || []);
    } catch (e) {
      setListError(e?.response?.data?.error || e.message || 'Failed to load scheduler jobs.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const fetchWaTemplates = useCallback(async () => {
    setWaTemplatesLoading(true);
    setWaTemplatesError('');
    try {
      const res = await CustomAxios.get('oasis/whatsapp/templates/');
      setWaTemplates(res.data?.data || []);
    } catch (e) {
      setWaTemplatesError(e?.response?.data?.error || e.message || 'Failed to load WhatsApp templates.');
      setWaTemplates([]);
    } finally {
      setWaTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (dialogOpen) {
      fetchWaTemplates();
    }
  }, [dialogOpen, fetchWaTemplates]);

  const templateDropdownValue = (() => {
    const match = waTemplates.some(
      (t) =>
        (t.name || '').trim() === (formTemplateName || '').trim() &&
        String(t.language || '').trim() === (formTemplateLanguage || '').trim()
    );
    return match ? templateSelectValue(formTemplateName, formTemplateLanguage) : '';
  })();

  const openCreate = () => {
    setEditingId(null);
    setFormJobName('');
    setFormEnabled(false);
    setFormTemplateName('');
    setFormTemplateLanguage('');
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setFormJobName(row.job_name || '');
    setFormEnabled(Boolean(row.is_enabled));
    setFormTemplateName(row.whatsapp_template_name || '');
    setFormTemplateLanguage(row.whatsapp_template_language || '');
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingId(null);
    setFormError('');
  };

  const handleSave = async () => {
    const name = formJobName.trim();
    if (!name) {
      setFormError('Job name is required.');
      return;
    }
    if (name.length > 50) {
      setFormError('Job name must be at most 50 characters.');
      return;
    }
    const actor = auditActor();
    setSaving(true);
    setFormError('');
    try {
      if (editingId == null) {
        await CustomAxios.post('oasis/scheduler-config/', {
          job_name: name,
          is_enabled: formEnabled,
          whatsapp_template_name: formTemplateName.trim() || null,
          whatsapp_template_language: formTemplateLanguage.trim() || null,
          created_by: actor,
          updated_by: actor,
        });
      } else {
        await CustomAxios.put(`oasis/scheduler-config/${editingId}/`, {
          job_name: name,
          is_enabled: formEnabled,
          whatsapp_template_name: formTemplateName.trim() || null,
          whatsapp_template_language: formTemplateLanguage.trim() || null,
          updated_by: actor,
        });
      }
      setDialogOpen(false);
      setEditingId(null);
      await fetchList();
    } catch (e) {
      setFormError(e?.response?.data?.error || e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (row) => {
    const actor = auditActor();
    try {
      await CustomAxios.put(`oasis/scheduler-config/${row.id}/`, {
        job_name: row.job_name,
        is_enabled: !row.is_enabled,
        whatsapp_template_name: row.whatsapp_template_name || null,
        whatsapp_template_language: row.whatsapp_template_language || null,
        updated_by: actor,
      });
      await fetchList();
    } catch (e) {
      setListError(e?.response?.data?.error || e.message || 'Update failed.');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await CustomAxios.delete(`oasis/scheduler-config/${deleteTarget.id}/`);
      setDeleteTarget(null);
      await fetchList();
    } catch (e) {
      setListError(e?.response?.data?.error || e.message || 'Delete failed.');
    } finally {
      setSaving(false);
    }
  };

  const formatDt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <Box sx={{ width: '100%', marginTop: '30px', padding: '27px'}}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/communication/whatsapp')}
          sx={{ color: '#288d2c', minWidth: 0, px: 1 }}
        />
        <ScheduleOutlinedIcon sx={{ fontSize: 32, color: '#288d2c' }} />
        <Typography
          sx={{
            color: 'var(--Pallate-green-500, #288d2c)',
            fontFamily: 'inter',
            fontSize: '24px',
            fontWeight: 500,
          }}
        >
          Trigger configuration
        </Typography>
      </Box>

      <Typography sx={{ fontFamily: 'inter', fontSize: '15px', color: '#475569', lineHeight: 1.6, mb: 3 }}>
        Manage named scheduler jobs stored in Oasis. The Azure Function timer can still use environment variables;
        these records let you track and toggle jobs from the admin UI.
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid #E2E8F0',
          fontFamily: 'inter',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#1E293B' }}>
            Scheduler jobs
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={fetchList}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontFamily: 'inter',
                borderColor: '#CBD5E1',
                color: '#1E293B',
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{
                textTransform: 'none',
                fontFamily: 'inter',
                backgroundColor: '#288d2c',
                '&:hover': { backgroundColor: '#1f6b22' },
              }}
            >
              Add job
            </Button>
          </Box>
        </Box>

        {listError && (
          <Typography sx={{ color: '#DC2626', fontSize: '14px', mb: 2, whiteSpace: 'pre-wrap' }}>
            {listError}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: '#288d2c' }} />
          </Box>
        ) : (
          <Table size="small" sx={{ '& th': { fontFamily: 'inter', fontWeight: 600, color: '#475569' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Job name</TableCell>
                <TableCell>WhatsApp template</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ color: '#94A3B8', fontStyle: 'italic' }}>
                    No jobs yet. Add one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover sx={{ '& td': { fontFamily: 'inter', fontSize: '14px' } }}>
                    <TableCell>{row.job_name}</TableCell>
                    <TableCell sx={{ maxWidth: 220, fontSize: '13px', color: '#64748B' }}>
                      {row.whatsapp_template_name || '—'}
                      {row.whatsapp_template_language ? ` (${row.whatsapp_template_language})` : ''}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={Boolean(row.is_enabled)}
                        onChange={() => handleToggleEnabled(row)}
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" aria-label="Edit" onClick={() => openEdit(row)} sx={{ color: '#288d2c' }}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Delete"
                        onClick={() => setDeleteTarget(row)}
                        sx={{ color: '#DC2626' }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

     

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontFamily: 'inter', fontWeight: 600 }}>
          {editingId == null ? 'Add scheduler job' : 'Edit scheduler job'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {formError && (
            <Typography sx={{ color: '#DC2626', fontSize: '13px', mb: 2 }}>
              {formError}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Job name"
            fullWidth
            value={formJobName}
            onChange={(e) => setFormJobName(e.target.value)}
            inputProps={{ maxLength: 50 }}
            sx={{ mb: 2, fontFamily: 'inter' }}
          />
          <FormControl fullWidth margin="dense" sx={{ mb: 1 }}>
            <InputLabel id="wa-template-select-label">WhatsApp template (from Meta)</InputLabel>
            <Select
              labelId="wa-template-select-label"
              label="WhatsApp template (from Meta)"
              value={templateDropdownValue}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  setFormTemplateName('');
                  setFormTemplateLanguage('');
                  return;
                }
                const { name: tn, language: tl } = parseTemplateSelectValue(v);
                setFormTemplateName(tn);
                setFormTemplateLanguage(tl);
              }}
              disabled={waTemplatesLoading}
            >
              <MenuItem value="">
                <em>None — enter manually below</em>
              </MenuItem>
              {waTemplates.map((t) => {
                const lang =
                  typeof t.language === 'string'
                    ? t.language
                    : t.language && typeof t.language === 'object'
                      ? t.language.code || ''
                      : '';
                const optVal = templateSelectValue(t.name, lang);
                return (
                  <MenuItem key={t.id != null ? String(t.id) : optVal} value={optVal}>
                    {(t.name || '—') + (lang ? ` (${lang})` : '')}
                    {t.status ? ` — ${t.status}` : ''}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {waTemplatesLoading && (
            <Typography sx={{ fontSize: '12px', color: '#64748B', mb: 2 }}>Loading templates…</Typography>
          )}
          {waTemplatesError && (
            <Typography sx={{ fontSize: '12px', color: '#DC2626', mb: 2 }}>{waTemplatesError}</Typography>
          )}
          <TextField
            margin="dense"
            label="WhatsApp template name"
            fullWidth
            value={formTemplateName}
            onChange={(e) => setFormTemplateName(e.target.value)}
            inputProps={{ maxLength: 128 }}
            placeholder="e.g. birthday_wish"
            sx={{ mb: 2, fontFamily: 'inter' }}
            helperText="Filled from the dropdown above, or type a Meta template name."
          />
          <TextField
            margin="dense"
            label="Template language code"
            fullWidth
            value={formTemplateLanguage}
            onChange={(e) => setFormTemplateLanguage(e.target.value)}
            inputProps={{ maxLength: 20 }}
            placeholder="e.g. en_US"
            sx={{ mb: 2, fontFamily: 'inter' }}
            helperText="Filled from the dropdown (Meta language code)."
          />
          <FormControlLabel
            control={
              <Switch checked={formEnabled} onChange={(e) => setFormEnabled(e.target.checked)} color="success" />
            }
            label="Enabled"
            sx={{ fontFamily: 'inter' }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: 'none', fontFamily: 'inter' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontFamily: 'inter',
              backgroundColor: '#288d2c',
              '&:hover': { backgroundColor: '#1f6b22' },
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !saving && setDeleteTarget(null)}>
        <DialogTitle sx={{ fontFamily: 'inter' }}>Delete job?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'inter', fontSize: '14px', color: '#475569' }}>
            Remove &ldquo;{deleteTarget?.job_name}&rdquo;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={saving} sx={{ textTransform: 'none' }}>
            {saving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CommunicationWhatsAppTriggerConfig;
